import { CDSOption } from '@ciscodesignsystems/cds-react-select';
import {
  find,
  isEmpty,
  uniqBy,
  capitalize,
  uniq,
  intersection,
  groupBy,
} from 'lodash';
import { createSelector } from 'reselect';
import { AppliesTo, GroupType } from 'src/app/enums/Groups';
import { UserRoleType } from 'src/app/enums/UserRoles';
import {
  IRbacUserRole,
  IRoleToGroupRelationResp,
} from 'src/app/interfaces/IUserRoles';
import {
  filterRolesBySaseState,
  IProductInstanceInfo,
} from 'src/app/utils/saseRoleFilter';
import { getKPAManagedTenantIdsHelper, sliceTableData } from './helper';
import {
  IRbacAssignedRole,
  IRbacGroup,
  IRbacTableDataGroup,
  IRbacTableDataRole,
  IDependentOption,
  IAssigneRoleRow,
  IAssignedRole,
  IUserFormRow,
  IRbacGroupWithoutInstances,
} from './interfaces';
import {
  filterProductsByUnselected,
  getAssignedRolesForGroup,
  removeProductsOptionsWithoutUnassignedRoles,
  removeUserRolesFromRolesOptions,
  sendOrgsNameArray,
  toProductOptions,
  toRoleOptions,
} from './utilities';
import { IState } from '../../core/redux/interfaces';
import { EnterpriseRegionType } from '../../enums/EnterpriseType';
import { ProvisioningStatus } from '../../enums/ProvisioningStatus';
import { RolesPermission } from '../../enums/RolesPermissionList';
import {
  IUser,
  IProductInstance,
  IProductInstanceOption,
} from '../../interfaces/ICommon';
import {
  IAssignedRoleWIthInstanceTableRow,
  IInstanceOption,
  IRolesWithInstances,
  IUsers,
} from '../../interfaces/IUsers';
import {
  FEATURE_FLAG_CONSTANTS,
  ENTERPRISE,
  ENTERPRISE_PRODUCT_TYPE,
  ENTERPRISE_PRODUCT_NAME,
  ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
  ORGANIZATION_ADMINISTRATOR,
  FEATURE_FLAG_PREFIX,
  ENTERPRISE_ADMIN_ROLE,
} from '../../utils/constant';
import {
  getTablePaginationPagesCount,
  composeFunctions,
  sortTableData,
  filterProductOptionsForNewRow,
} from '../../utils/shared';
import {
  classifyGroupsForKpa,
  convertRolesToInstanceDictionary,
  filteredNonBundledRoles,
  filterOutRolesByFeatureFlag,
  filterRolesByExistingProducts,
  getProductInstanceDisplayName,
  getProvisionedProductInstances,
} from '../common/helpers';
import {
  getAllUsers,
  getAllProductsInstances,
  getCurrentUserId,
  hasFeatureFlags,
  getAllProvisionedProductsInstances,
  getAllGroups,
  getAllRoles,
  getFeatureFlagsSet,
  getProductInstances,
} from '../common/selectors';

export interface IOption {
  label: string;
  value: string;
  selected?: boolean;
}

export const getState = (state: IState): IUsers => state.users || {};

export const getUsersTableActiveTab = createSelector(
  [getState],
  ({ usersTableActiveTab }) => usersTableActiveTab
);

export const getUserDetails = createSelector(
  [getState],
  ({ userDetails }) => userDetails
);

export const getEditUserDetails = createSelector(
  [getState],
  ({ userDetails }) => userDetails
);

export const getDefaultSccRoleFromDrawer = createSelector(
  [getUserDetails],
  (userDetails) => {
    return userDetails?.isAdmin
      ? RolesPermission.ADMINISTRATOR
      : RolesPermission.MEMBER;
  }
);

export const getCurrentEditUserId = createSelector(
  [getEditUserDetails],
  ({ id }) => id
);

export const getEditUserDetailsDrawerFields = createSelector(
  [getState],
  ({ editUserDetailsDrawerFields }) => editUserDetailsDrawerFields
);

export const getIsUserDetailsDrawerVisible = createSelector(
  [getState],
  ({ isUserDetailsDrawerVisible }) => isUserDetailsDrawerVisible
);

export const getIsLoading = createSelector(
  [getState],
  ({ isLoading }) => isLoading
);

export const getIsCreateGroupLoading = createSelector(
  [getState],
  ({ isCreateGroupLoading }) => isCreateGroupLoading
);

export const getActiveEditUserId = createSelector(
  [getState],
  ({ activeEditUserId }) => activeEditUserId
);

export const getIsUsersFileUploaded = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.isUsersFileUploaded
);

export const getInviteUsers = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.inviteUsers
);

export const getSelectedProducts = createSelector(
  [getState],
  ({ inviteStepper }) =>
    inviteStepper.assignRolesTable
      .map((row) => row.selectedProduct)
      .filter(Boolean)
);

export const getSelectedRolesIDs = createSelector(
  [getState],
  ({ inviteStepper }) => {
    const selectedRolesValues: string[] = [];

    for (const row of inviteStepper.assignRolesTable) {
      if (row.selectedProduct) {
        const selectedProductRoles = row.rolesOptions[row.selectedProduct];
        const rolesIDs = selectedProductRoles
          .filter((role: IOption) => role.selected)
          .map((role: IOption) => role.value);
        selectedRolesValues.push(...rolesIDs);
      }
    }

    return selectedRolesValues;
  }
);

export const getSelectedRolesIDsWithInstances = createSelector(
  [getState],
  ({ inviteStepper }) => {
    return inviteStepper.assignRolesWithInstancesTable.reduce(
      (acc: any, row) => {
        if (!row.selectedRole) return acc;
        const instanceOptions = Object.values(row.instancesOptions);
        instanceOptions.forEach((roleOptions: any) => {
          roleOptions.forEach((option: IInstanceOption) => {
            if (option.selected) {
              acc.push({
                roleId: option.roleId,
                tenantId: option.value,
              });
            }
          });
        });
        return acc;
      },
      []
    );
  }
);

export const getAssignRolesStepRows = createSelector(
  [getState],
  ({ inviteStepper }) => {
    return filterProductsByUnselected(inviteStepper.assignRolesTable);
  }
);

export const getAssignRolesWithInstancesStepRows = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.assignRolesWithInstancesTable
);

export const getIsFinishAndInviteEnabled = createSelector(
  [getState],
  ({ inviteStepper }) =>
    Object.values(inviteStepper.rolesOptions).some((availableRoles: any) =>
      availableRoles.some(({ selected }: IOption) => selected)
    )
);

export const getIsGroupDrawerVisible = createSelector(
  [getState],
  ({ isGroupDrawerVisible }) => isGroupDrawerVisible
);

export const getEditUser = createSelector(
  [getState],
  ({ editUser }) => editUser
);

export const getAssignedRoles = createSelector(
  [getEditUser],
  ({ roles }) => roles
);

// Filters ONLY STATIC roles by Tenants. Roles with other types returns as is.
// Filter based on provisioned Product instances.
// Check "filterRolesByExistingProducts()".
export const getAllRolesByExistingProducts = createSelector(
  [getAllRoles, getAllProvisionedProductsInstances],
  (allRoles, allProvisionedProductsInstances) =>
    filterRolesByExistingProducts(
      allRoles,
      allProvisionedProductsInstances,
      false
    )
);

export const getAllRolesByExistingProductsWithoutInstances = createSelector(
  [getAllRoles, getAllProvisionedProductsInstances],
  (allRoles, allProvisionedProductsInstances) =>
    filterRolesByExistingProducts(
      allRoles,
      allProvisionedProductsInstances,
      true
    )
);

export const getAllRolesByExistingProductsFilteredBySase = createSelector(
  [getAllRolesByExistingProducts, getProductInstances],
  (roles, productInstances) => {
    return filterRolesBySaseState(
      roles,
      productInstances as IProductInstanceInfo[]
    );
  }
);

export const getRolesWithProvisionedInstances = createSelector(
  [
    getAllRolesByExistingProductsFilteredBySase,
    getAllProductsInstances,
    (state) =>
      hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.IAM_SYSTEM_GROUPS_10194]),
  ],
  (allRoles, { items: allProductsInstances }, hasSystemGroupFF) => {
    const filteredRoles = filteredNonBundledRoles(allRoles, hasSystemGroupFF);
    const provisionedInstancesByProductKey = groupBy(
      getProvisionedProductInstances(allProductsInstances),
      'productType'
    );
    const rolesWithInstances = filteredRoles.reduce<IRolesWithInstances[]>(
      (acc, role) => {
        const instances = role.productKey
          ? provisionedInstancesByProductKey[role.productKey]
          : [];

        if (!instances?.length) return acc;

        acc.push({
          ...role,
          productAndRole: `${role?.product} - ${role?.roleDisplayName}`,
          instances,
        });

        return acc;
      },
      []
    );

    return rolesWithInstances;
  }
);

export const getProductsAndRolesOptions = createSelector(
  [getRolesWithProvisionedInstances],
  (rolesWithInstances) => {
    const productsAndRolesOptions = rolesWithInstances.map(
      (roleWithInstances) => {
        return {
          label: roleWithInstances.productAndRole,
          value: roleWithInstances.id,
          selected: false,
          isProductProvisioned: roleWithInstances.isProductProvisioned,
        };
      }
    );
    return [
      ...productsAndRolesOptions,
      {
        value: ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
        label: ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
        selected: false,
      },
    ];
  }
);

export const getSelectedProductsAndRoles = createSelector(
  [getAssignRolesWithInstancesStepRows],
  (assignRolesWithInstancesStepRows) =>
    assignRolesWithInstancesStepRows.reduce<string[]>((acc, row) => {
      if (row.selectedRole) {
        acc.push(row.selectedRole);
      }
      return acc;
    }, [])
);

export const getInstancesOptions = createSelector(
  [
    getRolesWithProvisionedInstances,
    (state) => hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.BUNDLED_ROLE]),
  ],
  (rolesWithInstances, useRoleDescriptionForProductInstanceDisplay) => {
    const instancesOptions = rolesWithInstances.reduce<IDependentOption>(
      (acc, role) => {
        acc[role.productAndRole] = role.instances.map((instance) => {
          return {
            value: instance.id,
            roleId: role.id,
            label: getProductInstanceDisplayName(
              instance,
              useRoleDescriptionForProductInstanceDisplay
            ),
            selected: false,
          };
        });
        return acc;
      },
      {}
    );
    return {
      ...instancesOptions,
      [ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME]: [
        {
          value: ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
          roleId: ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
          label: `Enterprise`,
          selected: false,
        },
      ],
    };
  }
);

export const getAssignRolesDrawer = createSelector(
  [getEditUser],
  ({ assignRolesDrawer }) => assignRolesDrawer
);
export const getAssignRolesDrawerTable = createSelector(
  [getAssignRolesDrawer],
  ({ assignRolesTable }) => assignRolesTable
);

export const getAssignRolesWithInstancesTable = createSelector(
  [getAssignRolesDrawer],
  ({ assignRolesWithInstancesTable }) => assignRolesWithInstancesTable
);

export const getSharedUsersOrgIds = createSelector(
  [getState],
  ({ sharedUsersOrgIds }) => sharedUsersOrgIds
);
export const getSharedGroupsOrgIds = createSelector(
  [getState],
  ({ sharedGroupOrgIds }) => sharedGroupOrgIds
);
export const getSelectedProductsAndRolesInDrawer = createSelector(
  [getAssignRolesWithInstancesTable],
  (assignRolesWithInstancesTable) =>
    assignRolesWithInstancesTable.map((row) => row.selectedRole).filter(Boolean)
);

export const getUnassignedInstancesOptions = createSelector(
  [
    getRolesWithProvisionedInstances,
    getAssignedRoles,
    getDefaultSccRoleFromDrawer,
    (state) => hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.BUNDLED_ROLE]),
  ],
  (
    rolesWithInstances,
    assignedRoles,
    sccRole,
    useRoleDescriptionForProductInstanceDisplay
  ) => {
    const unassignedInstancesOptions = rolesWithInstances.reduce<
      Record<string, IProductInstanceOption[]>
    >((acc, role) => {
      const unassignedInstances = role.instances.filter((instance) => {
        return !assignedRoles.find((assignedRoleAndInstanceIds) => {
          return (
            assignedRoleAndInstanceIds.roleId === role.id &&
            (assignedRoleAndInstanceIds.tenantId === instance.id ||
              instance.id === ENTERPRISE_PRODUCT_TYPE)
          );
        });
      });

      if (!unassignedInstances.length) return acc;

      acc[role.productAndRole] = unassignedInstances.map((instance) => {
        return {
          value: instance.id,
          label: getProductInstanceDisplayName(
            instance,
            useRoleDescriptionForProductInstanceDisplay
          ),
          roleId: role.id,
          selected: false,
        };
      });
      return acc;
    }, {});
    if (sccRole === RolesPermission.ADMINISTRATOR) {
      return unassignedInstancesOptions;
    } else {
      return {
        ...unassignedInstancesOptions,
        [ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME]: [
          {
            value: ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
            roleId: '',
            label: `Enterprise`,
            selected: false,
          },
        ],
      };
    }
  }
);

export const getUnassignedProductsAndRolesOptions = createSelector(
  [getUnassignedInstancesOptions],
  (unassignedInstancesOptions) =>
    Object.keys(unassignedInstancesOptions).map((productAndRoleName) => {
      return {
        label: productAndRoleName,
        value: productAndRoleName,
        selected: false,
      };
    })
);

export const getSccRoleOptionsWithInstances = createSelector(
  [getAssignRolesDrawer],
  ({ sccRole }) => {
    return [
      {
        value: RolesPermission.MEMBER,
        label: `${ENTERPRISE_PRODUCT_NAME} - ${RolesPermission.MEMBER}`,
        selected: sccRole === RolesPermission.MEMBER,
      },
      {
        value: RolesPermission.ADMINISTRATOR,
        label: `${ENTERPRISE_PRODUCT_NAME} - ${RolesPermission.ADMINISTRATOR}`,
        selected: sccRole === RolesPermission.ADMINISTRATOR,
      },
    ];
  }
);

export const getSelectedInstancesIDsInDrawer = createSelector(
  [getAssignRolesWithInstancesTable],
  (assignRolesWithInstancesTable) => {
    const selectedInstancesIDsInDrawer: Array<{
      roleId: string;
      instanceId: string;
    }> = assignRolesWithInstancesTable.reduce((acc: any, row) => {
      if (!row.selectedRole) return acc;
      const instanceOptions = Object.values(row.instancesOptions);
      instanceOptions.forEach((roleOptions: any) => {
        roleOptions.forEach((option: IInstanceOption) => {
          if (option.selected) {
            acc.push({
              roleId: option.roleId,
              instanceId: option.value,
            });
          }
        });
      });
      return acc;
    }, []);
    return Array.from(
      new Set(
        selectedInstancesIDsInDrawer.map((item: Object) => JSON.stringify(item))
      )
    ).map((item) => JSON.parse(item));
  }
);

export const getSccRoleOptions = createSelector(
  [getAssignRolesDrawer],
  ({ sccRole }) => {
    return [
      {
        value: RolesPermission.MEMBER,
        label: RolesPermission.MEMBER,
        selected: sccRole === RolesPermission.MEMBER,
      },
      {
        value: RolesPermission.ADMINISTRATOR,
        label: RolesPermission.ADMINISTRATOR,
        selected: sccRole === RolesPermission.ADMINISTRATOR,
      },
    ];
  }
);

export const getSelectedProductsInAssignRolesDrawer = createSelector(
  [getAssignRolesDrawer],
  ({ assignRolesTable }) =>
    assignRolesTable.map((row) => row.selectedProduct).filter(Boolean)
);

export const getSelectedRolesIDsInAssignRolesDrawer = createSelector(
  [getAssignRolesDrawer],
  ({ assignRolesTable }) => {
    const selectedRolesValues: string[] = [];

    for (const row of assignRolesTable) {
      if (row.selectedProduct) {
        const selectedProductRoles = row.rolesOptions[row.selectedProduct];
        const rolesIDs = selectedProductRoles
          .filter((role: IOption) => role.selected)
          .map((role: IOption) => role.value);
        selectedRolesValues.push(...rolesIDs);
      }
    }

    return selectedRolesValues;
  }
);

export const getSccRoleFromDrawer = createSelector(
  [getAssignRolesDrawer],
  ({ sccRole }) => sccRole
);

export const getIsSCCRoleChanged = createSelector(
  [getUserDetails, getSccRoleFromDrawer],
  (userDetails, sccRole) => {
    const defaultSccRoleFromDrawer = userDetails?.isAdmin
      ? RolesPermission.ADMINISTRATOR
      : RolesPermission.MEMBER;
    return defaultSccRoleFromDrawer !== sccRole;
  }
);

export const getAssignedRolesTableData = createSelector(
  [
    getAllRolesByExistingProductsFilteredBySase,
    getAssignedRoles,
    getDefaultSccRoleFromDrawer,
  ],
  (allRoles, assignedRoles, sccRole) => {
    const matchedRoles = allRoles
      .map((role: IRbacUserRole) => {
        const matchedRole = assignedRoles.find(
          (assignedRole: IRbacAssignedRole) => role.id === assignedRole.roleId
        );

        const output = matchedRole
          ? {
              id: matchedRole.id,
              name: role.roleDisplayName,
              product: role.product,
            }
          : false;

        return output;
      })
      .filter(Boolean) as IRbacUserRole[];

    const tableData = [
      {
        id: '',
        role: sccRole,
        product: ENTERPRISE,
      },
      ...matchedRoles.map((role: IRbacUserRole) => ({
        id: role.id,
        role: role.name,
        product: role.product,
      })),
    ];

    return tableData;
  }
);

export const getAssignedRolesWithInstancesTableData = createSelector(
  [
    getSharedUsersOrgIds,
    getAllRolesByExistingProductsFilteredBySase,
    getAssignedRoles,
    getDefaultSccRoleFromDrawer,
    getAllProductsInstances,
    (state) => hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.BUNDLED_ROLE]),
  ],
  (
    sharedUsersOrgIds,
    allRoles,
    assignedRoles,
    sccRole,
    allProductsInstancesState,
    useRoleDescriptionForProductInstanceDisplay
  ) => {
    const assignedRolesTableData = assignedRoles
      .map((assignedRole) => {
        const matchedRole = allRoles.find(
          (role) => role.id === assignedRole.roleId
        );
        const matchedInstance = allProductsInstancesState.items.find(
          (instance) => instance.id === assignedRole.tenantId
        );

        if (!matchedRole) {
          return null;
        }

        const baseData = {
          id: assignedRole.id,
          roleType: capitalize(matchedRole.type),
          product: matchedRole.product,
          productType: matchedRole.productKey,
          delegatable: matchedRole.delegatable,
        };

        // static & custom roles
        if (matchedInstance) {
          const tenantName = getProductInstanceDisplayName(
            matchedInstance,
            useRoleDescriptionForProductInstanceDisplay
          );
          return {
            ...baseData,
            role: matchedRole.roleDisplayName,
            productAndRole: `${
              matchedRole.productKey === ENTERPRISE_PRODUCT_TYPE
                ? ENTERPRISE_PRODUCT_NAME
                : matchedRole.product
            }`,
            instanceName: tenantName,
            region: matchedInstance.region.name,
            tenantId: matchedInstance.id,
          };
        }

        // bundled roles
        if (matchedRole.productRoles) {
          return {
            ...baseData,
            role: matchedRole.roleDisplayName,
            productAndRole: `${matchedRole.product}`,
            instanceName: ENTERPRISE,
            region: EnterpriseRegionType.GLOBAL,
          };
        }

        return null;
      })
      .filter(Boolean) as IAssignedRoleWIthInstanceTableRow[];
    let managedOrgDetails: any[] = [];
    if (sharedUsersOrgIds?.managedOrgs?.length > 0) {
      const sharedOrgsName: any = sendOrgsNameArray(
        sharedUsersOrgIds?.managedOrgs
      );
      if (sharedOrgsName.length > 0) {
        managedOrgDetails = sharedOrgsName.map(
          (managedOrg: { id: string; name: string }) => {
            return {
              id: sharedUsersOrgIds?.managedOrgs[0].id,
              role: sccRole,
              product: ENTERPRISE,
              productType: ENTERPRISE_PRODUCT_TYPE,
              productAndRole: `${ENTERPRISE_PRODUCT_NAME} - ${ORGANIZATION_ADMINISTRATOR}`,
              instanceName: managedOrg,
              region: EnterpriseRegionType.GLOBAL,
              roleType: capitalize(UserRoleType.STATIC),
              delegatable: false,
              orgShared: true,
            };
          }
        );
      }
    }

    const tableData: IAssignedRoleWIthInstanceTableRow[] = [
      {
        id: '',
        role: sccRole,
        product: ENTERPRISE,
        productType: ENTERPRISE_PRODUCT_TYPE,
        productAndRole: `${ENTERPRISE_PRODUCT_NAME}`,
        instanceName: ENTERPRISE,
        region: EnterpriseRegionType.GLOBAL,
        roleType: capitalize(UserRoleType.STATIC),
        delegatable: false,
      },
      ...managedOrgDetails,
      ...assignedRolesTableData,
    ];
    return tableData;
  }
);

export const getAssignedRolesTablePagination = createSelector(
  [getEditUser],
  ({ assignedRolesTablePagination }) => assignedRolesTablePagination
);

export const getFilteredAssignedRolesTableData = createSelector(
  [getAssignedRolesWithInstancesTableData, getEditUser],
  (
    tableData,
    {
      assignRolesTable: {
        search,
        productFilterSelectedOption,
        regionFilterSelectedOption,
        typesFilterSelectedOption,
        sortingOptions,
      },
    }
  ) => {
    const filterBySearch = (): IAssigneRoleRow[] => {
      const result = search
        ? tableData.filter(({ productAndRole, region }) => {
            const searchValue = search.toLowerCase().trim();
            const hasMatch =
              productAndRole?.toLowerCase().includes(searchValue) ||
              region?.toLowerCase().includes(searchValue);
            return hasMatch;
          })
        : tableData;
      return result as any;
    };

    const filterByProduct = (data: IAssigneRoleRow[]): IAssigneRoleRow[] => {
      const result = productFilterSelectedOption
        ? data.filter(({ productType }) => {
            return productType === productFilterSelectedOption.value;
          })
        : data;

      return result;
    };

    const filterByRegion = (data: IAssigneRoleRow[]): IAssigneRoleRow[] => {
      const result = regionFilterSelectedOption
        ? data.filter((role) => {
            return (
              role.region === regionFilterSelectedOption.value ||
              role.product === regionFilterSelectedOption.label
            );
          })
        : data;

      return result;
    };

    const filterByType = (data: IAssigneRoleRow[]): IAssigneRoleRow[] => {
      const result = typesFilterSelectedOption
        ? data.filter(
            (role) => role.roleType === typesFilterSelectedOption.value
          )
        : data;
      return result;
    };

    const composedFunction = composeFunctions(
      filterBySearch,
      filterByProduct,
      filterByRegion,
      filterByType
    ) as Function;

    const output = composedFunction();

    const sortedOutput = sortTableData<IAssigneRoleRow>(output, sortingOptions);
    return sortedOutput;
  }
);

export const getSlicedAssignedRolesTableData = createSelector(
  [getFilteredAssignedRolesTableData, getAssignedRolesTablePagination],
  (filteredAssignedRoles, { pageSize, pageIndex }) => {
    const slicedTableData = sliceTableData<IRbacTableDataRole>(
      pageIndex,
      pageSize,
      filteredAssignedRoles
    );

    return slicedTableData;
  }
);

export const getSlicedAssignedRoleswithInstancesTableData = createSelector(
  [getFilteredAssignedRolesTableData, getAssignedRolesTablePagination],
  (filteredAssignedRoles, { pageSize, pageIndex }) => {
    const slicedTableData = sliceTableData<IRbacTableDataRole>(
      pageIndex,
      pageSize,
      filteredAssignedRoles
    );

    return slicedTableData;
  }
);

export const getAssignedRolesTablePageCount = createSelector(
  [getFilteredAssignedRolesTableData, getAssignedRolesTablePagination],
  (assignedRoles, { pageSize }) => {
    return getTablePaginationPagesCount(assignedRoles.length, pageSize);
  }
);

export const getProductFilterSelectedOption = createSelector(
  [getEditUser],
  ({ assignRolesTable }) => assignRolesTable.productFilterSelectedOption
);

export const getProducts = createSelector(
  [getAllProductsInstances],
  ({ items }) => {
    const products = items.map(
      ({ productName, productType }: IProductInstance) => ({
        label: productName,
        value: productType,
      })
    );
    const result = [
      {
        label: ENTERPRISE_PRODUCT_NAME,
        value: ENTERPRISE_PRODUCT_TYPE,
      },
      ...products,
    ];

    return uniqBy(result, 'value');
  }
);

export const getRegionFilterSelectedOption = createSelector(
  [getEditUser],
  ({ assignRolesTable }) => assignRolesTable.regionFilterSelectedOption
);

export const getTypesFilterOptions = createSelector(
  [getEditUser, getAssignedRolesWithInstancesTableData],
  ({ assignRolesTable: { typesFilterOptions } }) => typesFilterOptions
);

export const getGroupTypesFilterSelectedOption = createSelector(
  [getEditUser],
  ({ groupsTable: { selectedTypeGroupOption } }) => selectedTypeGroupOption
);

export const getAssignedGroups = createSelector(
  [getEditUser],
  ({ groups }) => groups
);

export const getGroupsToRolesRelations = createSelector(
  [getState],
  ({ groupsToRolesRelations }) => groupsToRolesRelations
);

export const getAssignedGroupsTableData = createSelector(
  [
    getAllGroups,
    getAssignedGroups,
    getAllRolesByExistingProductsFilteredBySase,
    getGroupsToRolesRelations,
    getAllProductsInstances,
    getAllRolesByExistingProductsWithoutInstances,
    (state) =>
      hasFeatureFlags(state, [
        FEATURE_FLAG_CONSTANTS.MSP_ASSIGN_ROLE_WITHOUT_INSTANCE,
      ]),
  ],
  (
    allGroups,
    assignedGroups,
    allRoles,
    groupsToRolesRelations,
    { items: allProductsInstances },
    allRolesWithoutInstances,
    mspFeatureFlag
  ) => {
    if (isEmpty(assignedGroups) || groupsToRolesRelations?.loading) {
      return [];
    }

    const provisionedProductInstances =
      getProvisionedProductInstances(allProductsInstances);

    const tableData = assignedGroups.map((assignedGroup: IRbacGroup) => {
      const { id, groupId } = assignedGroup;
      const groupData: IRbacGroupWithoutInstances | undefined = find(
        allGroups,
        {
          id: groupId,
        }
      );

      if (!groupData) {
        return null;
      }
      const canAssignWithoutInstance =
        mspFeatureFlag &&
        (groupData?.appliesTo === AppliesTo.ALL_MANAGED ||
          groupData?.appliesTo === AppliesTo.SOME_MANAGED);
      const assignedRoles = getAssignedRolesForGroup(
        groupsToRolesRelations?.items,
        canAssignWithoutInstance ? allRolesWithoutInstances : allRoles,
        provisionedProductInstances,
        groupId
      );

      return {
        id,
        groupId,
        type: groupData?.type,
        name: groupData?.name,
        description: groupData?.description,
        assignedRoles,
      };
    });
    return tableData.filter((group) => !!group);
  }
);

export const getGroupsTablePagination = createSelector(
  [getEditUser],
  ({ groupsTablePagination }) => groupsTablePagination
);

export const getFilteredGroupsTableData = createSelector(
  [getAssignedGroupsTableData, getEditUser],
  (
    tableData,
    { groupsTable: { search, sortingOptions, selectedTypeGroupOption } }
  ) => {
    const filterDataBySearch = search
      ? tableData.filter(
          (group) =>
            group.name.toLowerCase().includes(search.toLowerCase()) ||
            Object.values(group.assignedRoles)
              .flat()
              .join(', ')
              .toLowerCase()
              .includes(search.toLowerCase())
        )
      : tableData;

    const filterDataByType = selectedTypeGroupOption
      ? filterDataBySearch.filter(
          (group) => group.type === selectedTypeGroupOption.value
        )
      : filterDataBySearch;

    const sortedData = sortTableData<IRbacTableDataGroup>(
      filterDataByType,
      sortingOptions
    );
    return sortedData;
  }
);

export const getAvailableGroupTypesOptions = createSelector(
  [getAssignedGroupsTableData],
  (assignedGroupsTableData) => {
    const uniqueTypes = new Set<string>();

    assignedGroupsTableData.forEach((group) => {
      if (group.type && typeof group.type === 'string') {
        const trimmedType = group.type.trim();
        if (trimmedType) {
          uniqueTypes.add(trimmedType);
        }
      }
    });

    return Array.from(uniqueTypes).map((type) => ({
      label: type,
      value: type,
    }));
  }
);

export const getSlicedGroupsTableData = createSelector(
  [getFilteredGroupsTableData, getGroupsTablePagination],
  (filteredGroups, { pageSize, pageIndex }) => {
    const slicedTableData = sliceTableData<IRbacTableDataGroup>(
      pageIndex,
      pageSize,
      filteredGroups
    );

    return slicedTableData;
  }
);

export const getGroupsTablePageCount = createSelector(
  [getFilteredGroupsTableData, getGroupsTablePagination],
  (assignedGroupsTableData, { pageSize }) => {
    return getTablePaginationPagesCount(
      assignedGroupsTableData.length,
      pageSize
    );
  }
);

export const getActiveRemoveRoleId = createSelector(
  [getEditUser],
  ({ activeRemoveRoleId }) => activeRemoveRoleId
);

export const getActiveRemoveRoleWithInstanceRowData = createSelector(
  [getEditUser],
  ({ activeRemoveRoleWithInstanceRowData }) =>
    activeRemoveRoleWithInstanceRowData
);

export const getActiveRemoveGroupId = createSelector(
  [getEditUser],
  ({ activeRemoveGroupId }) => activeRemoveGroupId
);

export const getActiveRemoveRoleDetails = createSelector(
  [getActiveRemoveRoleId, getAssignedRolesTableData],
  (activeRemoveRoleId, assignedRoles) =>
    assignedRoles.find((role) => role.id === activeRemoveRoleId)
);

export const getActiveRemoveRoleDetailsWithInstance = createSelector(
  [getActiveRemoveRoleId, getAssignedRolesWithInstancesTableData],
  (activeRemoveRoleId, assignedRoles) => {
    return assignedRoles.find((role) => role.id === activeRemoveRoleId);
  }
);

export const getActiveRemoveGroupDetails = createSelector(
  [getActiveRemoveGroupId, getAssignedGroupsTableData],
  (activeRemoveGroupId, assignedGroups) =>
    assignedGroups.find((group) => group.id === activeRemoveGroupId)
);

export const getIsAssignRolesDrawerVisible = createSelector(
  [getEditUser],
  ({ isAssignRolesDrawerVisible }) => isAssignRolesDrawerVisible
);

export const getIsAssignGroupsDrawerVisible = createSelector(
  [getEditUser],
  ({ isAssignGroupsDrawerVisible }) => isAssignGroupsDrawerVisible
);

export const getProductOptionsFromDrawer = createSelector(
  [getEditUser],
  ({ productOptionsFromDrawer }) => productOptionsFromDrawer
);

export const getPristineRolesOptionsFromDrawer = createSelector(
  [getEditUser],
  ({ pristineRolesOptionsFromDrawer }) => pristineRolesOptionsFromDrawer
);

export const getSelectedProductOptionFromDrawer = createSelector(
  [getProductOptionsFromDrawer],
  (productOptionsFromDrawer) =>
    productOptionsFromDrawer.find((option: CDSOption) => option.selected)
);

export const getRolesOptionsFromDrawer = createSelector(
  [getEditUser],
  ({ rolesOptionsFromDrawer }) => rolesOptionsFromDrawer
);

export const getSelectedRolesOptionsFromDrawer = createSelector(
  [getRolesOptionsFromDrawer],
  (rolesOptionsFromDrawer) =>
    rolesOptionsFromDrawer.filter((option: CDSOption) => option.selected)
);

export const getPristineProductOptionsFromDrawer = createSelector(
  [getProductOptionsFromDrawer],
  (productOptionsFromDrawer) =>
    productOptionsFromDrawer.map((option: CDSOption) => ({
      ...option,
      selected: false,
    }))
);

export const getIsFailureCreatedGroupNotificationVisible = createSelector(
  [getState],
  ({ isFailureCreatedGroupNotificationVisible }) =>
    isFailureCreatedGroupNotificationVisible
);

export const getGroupsOptionsFromDrawer = createSelector(
  [getEditUser],
  ({ groupsOptionsFromDrawer }) => groupsOptionsFromDrawer
);

export const getSelectedGroupsOptionsFromDrawer = createSelector(
  [getGroupsOptionsFromDrawer],
  (groupsOptionsFromDrawer) =>
    groupsOptionsFromDrawer.filter((option: CDSOption) => option.selected)
);

export const getPristineGroupsOptionsFromDrawer = createSelector(
  [getGroupsOptionsFromDrawer],
  (groupsOptionsFromDrawer) =>
    groupsOptionsFromDrawer.map((option: CDSOption) => ({
      ...option,
      selected: false,
    }))
);

export const getOpenedUser = createSelector(
  [getActiveEditUserId, getAllUsers],
  (activeEditUserId, allUsers) => {
    return allUsers.find((user: IUser) => user.id === activeEditUserId);
  }
);

export const getIsDiscardChangesModalVisible = createSelector(
  [getState],
  ({ isDiscardChangesModalVisible }) => isDiscardChangesModalVisible
);

export const getIsIncompleteUserDataModalVisible = createSelector(
  [getState],
  ({ isIncompleteUserDataModalVisible }) => isIncompleteUserDataModalVisible
);

export const getIsSuccessCreatedGroupNotificationVisible = createSelector(
  [getState],
  ({ isSuccessCreatedGroupNotificationVisible }) =>
    isSuccessCreatedGroupNotificationVisible
);

export const getIsErrorInviteUsersNotificationVisible = createSelector(
  [getState],
  ({ isBannerErrorInviteUsersVisible }) => isBannerErrorInviteUsersVisible
);

export const getUsersIsLoading = createSelector(
  [getState],
  ({ isLoading }) => isLoading
);

export const getUsersError = createSelector([getState], ({ error }) => error);

export const getNotificationType = createSelector(
  [getState],
  ({ notificationType }) => notificationType
);

export const getTelemetryErrorType = createSelector(
  [getState],
  ({ telemetryErrorType }) => telemetryErrorType
);

export const getAssignedRolesTableSearch = createSelector(
  [getEditUser],
  ({ assignRolesTable: { search } }) => search
);

export const getAssignedRolesTableSortingOptions = createSelector(
  [getEditUser],
  ({ assignRolesTable: { sortingOptions } }) => sortingOptions
);

export const getGroupsTableSearch = createSelector(
  [getEditUser],
  ({ groupsTable: { search } }) => search
);

export const getGroupsTableSortingOptions = createSelector(
  [getEditUser],
  ({ groupsTable: { sortingOptions } }) => sortingOptions
);

export const getAssignedRolesByUserId = createSelector(
  [getState],
  ({
    assignedRolesByUserId,
    assignedGroupRolesByUserId,
    fetchingUserDataIds,
  }) => {
    return {
      assignedRolesByUserId,
      assignedGroupRolesByUserId,
      fetchingUserDataIds,
    };
  }
);

export const getKPARoleAssignments = createSelector(
  [getState, getAllRolesByExistingProductsFilteredBySase, getCurrentUserId],
  ({ assignedRolesByUserId, assignedGroupRolesByUserId }, allRoles, userId) => {
    if (!userId) {
      return {
        kpaDirectlyAssignedRoles: [],
        kpaGroupRoles: [],
      };
    }

    const kpaDirectlyAssignedRoles = (
      assignedRolesByUserId[userId] ?? []
    ).filter((r) => {
      const rbacRole = allRoles.find((role) => role.id === r.roleId);

      return (
        rbacRole?.delegatable ||
        (rbacRole?.type === UserRoleType.BUNDLED &&
          rbacRole.productRoles?.some((productRole) => productRole.delegatable))
      );
    });

    const groupRoles = (assignedGroupRolesByUserId[userId] ?? [])
      .map((g) => g.mappedRoles)
      .flat();

    const kpaGroupRoles = groupRoles.filter((r) => {
      const rbacRole = allRoles.find((role) => role.id === r.roleId);

      return (
        rbacRole?.delegatable ||
        (rbacRole?.type === UserRoleType.BUNDLED &&
          rbacRole.productRoles?.some((productRole) => productRole.delegatable))
      );
    });

    return {
      kpaDirectlyAssignedRoles,
      kpaGroupRoles,
    };
  }
);

export const getKPAManagedTenantIds = createSelector(
  [
    getKPARoleAssignments,
    getAllRolesByExistingProductsFilteredBySase,
    getAllProductsInstances,
  ],
  ({ kpaDirectlyAssignedRoles, kpaGroupRoles }, allRoles, productInstances) => {
    const allAssignedRoles: Array<
      IRbacAssignedRole | IRoleToGroupRelationResp
    > = [...kpaDirectlyAssignedRoles, ...kpaGroupRoles];
    return getKPAManagedTenantIdsHelper(
      allAssignedRoles,
      allRoles,
      productInstances.items
    );
  }
);

export const getKPAManagedTenants = createSelector(
  [getKPAManagedTenantIds, getAllProductsInstances],
  (tenantIds, productInstances) => {
    return productInstances.items.filter(
      (i) =>
        tenantIds.includes(i.id) &&
        i.provisioningStatus === ProvisioningStatus.FINISHED
    );
  }
);

export const getIsKPA = createSelector([getKPAManagedTenantIds], (tenantIds) =>
  Boolean(tenantIds.length)
);

export const getKPAManagedUsers = createSelector(
  [
    getState,
    getAllUsers,
    getKPAManagedTenantIds,
    getAllRolesByExistingProductsFilteredBySase,
    getAllProductsInstances,
  ],
  (
    { assignedGroupRolesByUserId, assignedRolesByUserId },
    allUsers,
    kpaManagedTenantIds,
    allRoles,
    productInstances
  ) => {
    const partiallyManagedUserIds: string[] = [];
    const fullyManagedUserIds: string[] = [];

    const users = !kpaManagedTenantIds.length
      ? allUsers
      : allUsers.filter((user) => {
          const userAssignedRoles = assignedRolesByUserId[user.id] ?? [];
          const userAssignedGroupRoles =
            assignedGroupRolesByUserId[user.id] ?? [];

          if (!userAssignedRoles.length && !userAssignedGroupRoles.length)
            return false;

          const groupRoleTenantIds = userAssignedGroupRoles.length
            ? getKPAManagedTenantIdsHelper(
                userAssignedGroupRoles.flatMap((g) => g.mappedRoles),
                allRoles,
                productInstances.items
              )
            : [];

          const userTenantIds = uniq([
            ...userAssignedRoles.map((r) => r.tenantId),
            ...groupRoleTenantIds,
          ]);

          const tenantsIntersection = intersection(
            kpaManagedTenantIds,
            userTenantIds
          );

          if (!tenantsIntersection.length) return false;

          // User can have roles in other tenants we are not KPA for
          if (
            tenantsIntersection.length === kpaManagedTenantIds.length &&
            tenantsIntersection.length === userTenantIds.length
          ) {
            fullyManagedUserIds.push(user.id);
          } else {
            partiallyManagedUserIds.push(user.id);
          }

          return true;
        });

    return {
      users,
      partiallyManagedUserIds,
      fullyManagedUserIds,
    };
  }
);

export const getKPAManagedUsersOptions = createSelector(
  [getKPAManagedUsers],
  ({ users }) => {
    return users.map((user) => ({
      value: user.id,
      label: `${user.profile.firstName} ${user.profile.lastName} (${user.profile.email})`,
    }));
  }
);

export const getFilteredByExistingInstancesRoles = createSelector(
  [getAllProductsInstances, getIsKPA, getAllRoles, getKPAManagedTenants],
  (allInstances, isKPA, rbacRoles, kpaManagedTenants) => {
    const rolesWithSccAdmin = [...rbacRoles, ENTERPRISE_ADMIN_ROLE];

    const KPAProducts = Array.from(
      new Set(kpaManagedTenants.map(({ productType }) => productType))
    );

    const roles = isKPA
      ? rolesWithSccAdmin.filter((role) =>
          KPAProducts.includes(role.productKey)
        )
      : rolesWithSccAdmin;

    const rolesWithoutOrganizationRoles = filterOutRolesByFeatureFlag(
      roles,
      `${FEATURE_FLAG_PREFIX}${FEATURE_FLAG_CONSTANTS.ROLE_ENTERPRISE}`
    );

    const filteredByExistingInstances = filterRolesByExistingProducts(
      rolesWithoutOrganizationRoles,
      allInstances.items,
      false
    ).filter((role) => role.isProductProvisioned);

    return filteredByExistingInstances;
  }
);

export const getProductsOptions = createSelector(
  [getFilteredByExistingInstancesRoles],
  (filteredByExistingInstances) => {
    const productsOptions = toProductOptions(filteredByExistingInstances);

    return productsOptions;
  }
);

export const getRolesOptions = createSelector(
  [getFilteredByExistingInstancesRoles, getFeatureFlagsSet],
  (filteredByExistingInstances, featureFlagsSet) => {
    const hasSystemGroupFF = featureFlagsSet.has(
      FEATURE_FLAG_CONSTANTS.IAM_SYSTEM_GROUPS_10194
    );

    const filteredRoles = filteredNonBundledRoles(
      filteredByExistingInstances,
      hasSystemGroupFF
    );

    const rolesOptions = toRoleOptions(filteredRoles);

    return rolesOptions;
  }
);

export const getUnassignedRolesOptions = createSelector(
  [
    getState,
    getAssignedRoles,
    getDefaultSccRoleFromDrawer,
    getCurrentUserId,
    getUserDetails,
    getRolesOptions,
  ],
  (
    { inviteStepper },
    assignedRoles,
    defaultSccRoleFromDrawer,
    currentUserId,
    userDetails,
    rolesOptions
  ) => {
    const optionsWithoutAssignedRoles = removeUserRolesFromRolesOptions(
      rolesOptions,
      assignedRoles
    );
    const editUserIsCurrentSystemUser = currentUserId === userDetails?.id;
    if (
      (optionsWithoutAssignedRoles &&
        defaultSccRoleFromDrawer === RolesPermission.ADMINISTRATOR) ||
      editUserIsCurrentSystemUser
    ) {
      return {
        ...optionsWithoutAssignedRoles,
        [ENTERPRISE_PRODUCT_NAME]: [],
      };
    }
    return optionsWithoutAssignedRoles;
  }
);

export const getProductsOptionsWithUnassignedRoles = createSelector(
  [getState, getUnassignedRolesOptions, getProductsOptions],
  ({ inviteStepper }, unassignedRolesOptions, productsOptions) =>
    removeProductsOptionsWithoutUnassignedRoles(
      productsOptions,
      unassignedRolesOptions
    )
);

export const getProductsOptionsForNewRow = createSelector(
  [
    getProductsOptionsWithUnassignedRoles,
    getSelectedProductsInAssignRolesDrawer,
  ],
  (productsOptions, selectedProducts) =>
    filterProductOptionsForNewRow(productsOptions, selectedProducts)
);

export const getIsUserRolesAssignmentsLoading = createSelector(
  [getState, getCurrentUserId],
  ({ fetchingUserDataIds }, kpaAdminId) => {
    // Ignore currently logged in KPA cause of constant KPA data refetch
    if (
      fetchingUserDataIds.length === 1 &&
      kpaAdminId &&
      fetchingUserDataIds.includes(kpaAdminId)
    ) {
      return false;
    }

    return Boolean(fetchingUserDataIds.length);
  }
);

export const getKPAManagedGroups = createSelector(
  [getKPAManagedTenantIds, getAllGroups, getGroupsToRolesRelations],
  (tenantIds, allGroups, groupsToRolesRelations) => {
    return classifyGroupsForKpa(tenantIds, allGroups, groupsToRolesRelations);
  }
);

export const getKPAManagedGroupsOptions = createSelector(
  [getKPAManagedGroups, getState],
  (managedGroups, { inviteStepper }) => {
    const { fullyManagedGroups, noRolesGroups } = managedGroups;
    const groupIds = new Set([
      ...fullyManagedGroups.map((g) => g.id),
      ...noRolesGroups.map((g) => g.id),
    ]);
    return inviteStepper.groupsOptions.filter(
      (o) => groupIds.has(o.value) && o.type !== GroupType.System
    );
  }
);

export const getKPAManagedProductsAndRoles = createSelector(
  [getAllRolesByExistingProductsFilteredBySase, getKPAManagedTenants],
  (allRoles, tenants) => {
    const tenantsByProductKey = groupBy(tenants, 'productType');

    return allRoles.reduce<IRolesWithInstances[]>((acc, r) => {
      const productTenants = r.productKey
        ? tenantsByProductKey[r.productKey]
        : [];

      if (!productTenants?.length) return acc;

      const roleWithInstance: IRolesWithInstances = {
        ...r,
        productAndRole: `${r?.product} - ${r?.roleDisplayName}`,
        instances: productTenants,
      };

      acc.push(roleWithInstance);

      return acc;
    }, []);
  }
);

export const getKPAManagedProductsAndRolesOptions = createSelector(
  [getKPAManagedProductsAndRoles],
  (roles) => {
    return roles.map((r) => ({
      label: r.productAndRole,
      value: r.id,
      selected: false,
      isProductProvisioned: r.isProductProvisioned,
    }));
  }
);

export const getKPAManagedInstancesOptions = createSelector(
  [getKPAManagedProductsAndRoles],
  (rolesWithInstances) => {
    return convertRolesToInstanceDictionary(rolesWithInstances);
  }
);

export const getEditedUserManagedTenantsIds = createSelector(
  [getUserDetails, getAssignedRolesByUserId],
  (userDetails, { assignedRolesByUserId, assignedGroupRolesByUserId }) => {
    if (!userDetails?.id) return [];

    const userId = userDetails.id;

    const userRoles = assignedRolesByUserId[userId] ?? [];
    const userGroupRoles = assignedGroupRolesByUserId[userId] ?? [];

    if (!userRoles.length && !userGroupRoles?.length) return [];

    const userRolesManagedTenants = userRoles.map((role) => role.tenantId);
    const userGroupRolesManagedTenants = userGroupRoles
      .map((group) => {
        return group.mappedRoles.map((r) => r.tenantId);
      })
      .flat();

    const userTenantsIds = uniq([
      ...userRolesManagedTenants,
      ...userGroupRolesManagedTenants,
    ]);

    return userTenantsIds;
  }
);

export const getIsUserHasForeignTenants = createSelector(
  [getEditedUserManagedTenantsIds, getKPAManagedTenantIds],
  (userTenantsIds, kpaTenantsIds) => {
    if (!userTenantsIds.length) return false;

    const tenantsIdsIntersection = intersection(userTenantsIds, kpaTenantsIds);

    return tenantsIdsIntersection.length !== userTenantsIds.length;
  }
);

export const getKpaManagedEditedUserRoles = createSelector(
  [getKPAManagedTenantIds, getFilteredAssignedRolesTableData],
  (tenantIds, assignedRoles) => {
    return assignedRoles.filter((r) => {
      return 'tenantId' in r && tenantIds.includes(r.tenantId as string);
    });
  }
);

export const getSlicedKpaManagedEditedUserRoles = createSelector(
  [getKpaManagedEditedUserRoles, getAssignedRolesTablePagination],
  (filteredAssignedRoles, { pageSize, pageIndex }) => {
    return sliceTableData<IRbacTableDataRole>(
      pageIndex,
      pageSize,
      filteredAssignedRoles
    );
  }
);

export const getEditedUserRoles = createSelector(
  [
    getFilteredAssignedRolesTableData,
    getAssignedRolesWithInstancesTableData,
    getKpaManagedEditedUserRoles,

    (state) =>
      hasFeatureFlags(state, [
        FEATURE_FLAG_CONSTANTS.ROLES_MANAGEMENT_DELEGATION,
      ]),
    getIsKPA,
    (state) => hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.USER_MANAGEMENT]),
  ],
  (
    assignedRoles,
    assignedRolesWithInstances,
    kpaManagedUserAssignedRoles,
    hasRoleDelegationFF,
    isActiveKPA,
    hasTenantFeatureFlag
  ) => {
    if (isActiveKPA && hasRoleDelegationFF) {
      return kpaManagedUserAssignedRoles;
    }

    if (hasTenantFeatureFlag) {
      return assignedRolesWithInstances;
    }

    return assignedRoles;
  }
);

export const getProductFilterOptions = createSelector(
  [getEditUser, getProductFilterSelectedOption, getEditedUserRoles],
  (
    { assignRolesTable: { productFilterOptions } },
    productFilterSelectedOption,
    assignedUserRoles
  ) => {
    return (
      productFilterSelectedOption
        ? productFilterOptions.map((option) => {
            if (productFilterSelectedOption.value === option.value) {
              return productFilterSelectedOption;
            }
            return option;
          })
        : productFilterOptions
    ).filter((product) =>
      assignedUserRoles.some(
        (role) =>
          role.productType.toLowerCase() === product.value?.toLowerCase()
      )
    );
  }
);

export const getKpaManagedUserRolesTablePageCount = createSelector(
  [getKpaManagedEditedUserRoles, getAssignedRolesTablePagination],
  (assignedRoles, { pageSize }) => {
    return getTablePaginationPagesCount(assignedRoles.length, pageSize);
  }
);

export const getKpaManagedEditedUserGroups = createSelector(
  [getKPAManagedTenantIds, getFilteredGroupsTableData],
  (tenantIds, filteredGroups) => {
    const kpaManagedUserGroups = filteredGroups.filter((g) => {
      if (g.type === GroupType.System) {
        return false;
      }
      return g.assignedRoles.every((r) => tenantIds.includes(r.tenantId));
    });

    return kpaManagedUserGroups;
  }
);

export const getKpaManagedUserGroupsTablePageCount = createSelector(
  [getKpaManagedEditedUserGroups, getGroupsTablePagination],
  (groups, { pageSize }) => {
    return getTablePaginationPagesCount(groups.length, pageSize);
  }
);

export const getSlicedKPAManagedUserGroupsTableData = createSelector(
  [getKpaManagedEditedUserGroups, getGroupsTablePagination],
  (kpaManagedUserGroups, { pageSize, pageIndex }) => {
    return sliceTableData<IRbacTableDataGroup>(
      pageIndex,
      pageSize,
      kpaManagedUserGroups
    );
  }
);

export const getKPAManagedGroupsOptionsFromDrawer = createSelector(
  [getKPAManagedGroups, getGroupsOptionsFromDrawer],
  (managedGroups, groupsOptionsFromDrawer) => {
    const { fullyManagedGroups, noRolesGroups } = managedGroups;
    const groups = [...fullyManagedGroups, ...noRolesGroups];
    const groupIds = new Set(groups.map((g) => g.id));
    return groupsOptionsFromDrawer.filter(
      (o) => groupIds.has(o.value) && o.type !== GroupType.System
    );
  }
);

export const getKpaManagedInstancesOptionsForRolesDrawer = createSelector(
  [getUnassignedInstancesOptions, getKPAManagedTenantIds],
  (instances, tenantIds) => {
    return Object.entries(instances).reduce<
      Record<string, IProductInstanceOption[]>
    >((prev, curr) => {
      const [productName, productInstances] = curr;

      const kpaManagedProductInstances = productInstances.filter((i) =>
        tenantIds.includes(i.value)
      );

      if (!kpaManagedProductInstances.length) return prev;

      prev[productName] = kpaManagedProductInstances;

      return prev;
    }, {});
  }
);

export const getUserFromEnterprise = createSelector(
  [getAllUsers, getInviteUsers],
  (users, inviteUsers) => {
    const usersAlreadyExistInEnterprise: IUser[] = [];
    const usersNotExistInEnterprise: IUserFormRow[] = [];

    const getUsersEmails = inviteUsers.map((item) => item.email.value);

    users.forEach((user) => {
      if (getUsersEmails.includes(user.profile.email)) {
        usersAlreadyExistInEnterprise.push(user);
      }
    });

    inviteUsers.forEach((inviteUser) => {
      if (
        !users.some((user) => user.profile.email === inviteUser.email.value)
      ) {
        usersNotExistInEnterprise.push(inviteUser);
      }
    });

    return {
      usersAlreadyExistInEnterprise,
      usersNotExistInEnterprise,
    };
  }
);

export const getRoleToShowNotificationBannerForKPA = createSelector(
  [
    getKpaManagedEditedUserGroups,
    getActiveRemoveGroupId,
    getUserDetails,
    getKpaManagedEditedUserRoles,
    getCurrentUserId,
  ],
  (
    kpaManagedUserGroups,
    activeRemoveGroupId,
    userDetails,
    kpaManagedEditedUserRoles,
    currentUserId
  ) => {
    if (!userDetails?.id) return [];

    const userId = userDetails.id;

    const filteredGroupByRemoveId = kpaManagedUserGroups.reduce<
      IAssignedRole[]
    >((acc, item) => {
      if (item.id === activeRemoveGroupId) {
        acc.push(...item.assignedRoles);
      }
      return acc;
    }, []);

    const matchAssignedRole = filteredGroupByRemoveId.filter(
      (group) =>
        !kpaManagedEditedUserRoles.some(
          (role) => role.role === group.roleDisplayName
        )
    );

    if (userId === currentUserId) {
      return matchAssignedRole;
    } else return [];
  }
);

export const getKpaManagedProductsAndRolesOptionsForRolesDrawer =
  createSelector(
    [
      getUnassignedProductsAndRolesOptions,
      getKpaManagedInstancesOptionsForRolesDrawer,
    ],
    (productAndRoles, instancesOptionsDictionary) => {
      const kpaManagedProductsNames = Object.keys(instancesOptionsDictionary);

      return productAndRoles.filter((option) =>
        kpaManagedProductsNames.includes(option.label)
      );
    }
  );

/* ----------------- Invite Stepper ----------------- */
export const getUsersStepperStep = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.usersStepperStep
);

export const getFileName = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.uploadFile.fileName
);

export const getProgressBar = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.uploadFile.progressBar
);

export const getNewGroupName = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.newGroupName
);

export const getGroupsOptions = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.groupsOptions
);

export const getSelectedGroupsIDs = createSelector(
  [getState],
  ({ inviteStepper }) =>
    inviteStepper.groupsOptions
      .filter((item: IOption) => item.selected)
      .map((item: IOption) => item.value)
);

export const getPristineGroupsOptions = createSelector(
  [getState],
  ({ inviteStepper }) =>
    inviteStepper.groupsOptions.map((option: IOption) => ({
      ...option,
      selected: false,
    }))
);

export const getProductsOptionsForNewRowInStepper = createSelector(
  [getProductsOptions, getSelectedProducts],
  (productsOptions, selectedProducts) =>
    filterProductOptionsForNewRow(productsOptions, selectedProducts)
);

export const getSCCRole = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.sccRole
);

export const getIsPendingToCreateUsers = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.isPendingToCreateUsers
);
export const getInviteStepperCurrentStep = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.currentStep
);
export const getCreateUsersNotification = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.createUsersNotification
);
export const getInvitedUsersCount = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.createUsersResponse.invitedUsersCount
);
export const getIsSuccessInviteUsersNotificationVisible = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.isSuccessInviteUsersNotificationVisible
);

export const getFormFieldsForNewGroup = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.newGroup
);

export const getIsUserDetailsDirty = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.isUserDetailsDirty
);

export const getStepperInviteUsersMethod = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.inviteUsersMethod
);

export const getInviteStepperCreateUsersResponse = createSelector(
  [getState],
  ({ inviteStepper }) => inviteStepper.createUsersResponse
);

export const getInviteUsersErrorReason = createSelector(
  [getInviteStepperCreateUsersResponse],
  (createUsersResponse) => createUsersResponse?.errorReason
);

/* ----------------- Invite Stepper ----------------- */

export const getUserSearchValue = createSelector(
  [getState],
  ({ userSearchValue }) => userSearchValue
);

export const getIsErrorRoleAssignBannerVisible = createSelector(
  [getEditUser],
  ({ isErrorRoleAssignBannerVisible }) => isErrorRoleAssignBannerVisible
);
